"use client";

import { useEffect, useState } from "react";
import { Trash2, TriangleAlert } from "lucide-react";
import { apiClient, isPaidRequiredError, type AlertDoc } from "@/lib/api-client";
import { DESTRUCTIVE, RADIUS } from "@/lib/alert-theme";
import {
  IcyDialogShell,
  PrimaryDialogButton,
  SecondaryDialogButton,
  getDialogInputClass,
  DIALOG_LABEL_CLASS,
  DIALOG_ERROR_CLASS,
} from "./IcyDialogShell";

/**
 * DeleteAlertDialog — irreversible cascade delete with type-to-confirm gate.
 *
 * We deliberately DON'T use the agent confirmation flow (the agent path uses
 * _await_tool_confirmation → Redis BLPOP → ephemeral preview). This is a
 * direct REST call from the page, so the safety gate is UX-only: the user has
 * to type the alert's name to confirm.
 *
 * On success we redirect to xynth.com via window.location — parent page owns
 * the redirect decision.
 */
export function DeleteAlertDialog({
  open,
  onOpenChange,
  alert,
  onDeleted,
  onPaidRequired,
  viewMode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alert: AlertDoc;
  onDeleted: () => void;
  onPaidRequired?: () => void;
  viewMode?: string;
}) {
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTyped("");
      setError(null);
    }
  }, [open]);

  const typedMatches = typed.trim() === (alert.name ?? "").trim();

  const handleDelete = async () => {
    if (!typedMatches) return;
    console.info("[DeleteAlertDialog] delete confirmed", {
      alertId: alert._id,
      alertName: alert.name,
    });
    setDeleting(true);
    setError(null);
    try {
      await apiClient.deleteAlert(alert._id);
      console.info("[DeleteAlertDialog] delete ok", { alertId: alert._id });
      onDeleted();
    } catch (err: any) {
      console.error("[DeleteAlertDialog] delete failed", { alertId: alert._id, err });
      if (isPaidRequiredError(err)) {
        onOpenChange(false);
        onPaidRequired?.();
        setDeleting(false);
        return;
      }
      setError(err?.message || "Could not delete alert");
      setDeleting(false);
    }
  };

  return (
    <IcyDialogShell viewMode={viewMode}
      open={open}
      onOpenChange={deleting ? () => {} : onOpenChange}
      title="Delete this alert?"
      subtitle="This permanently removes the alert, its notebook, and its history"
      Icon={Trash2}
      footer={
        <>
          <SecondaryDialogButton
            onClick={() => onOpenChange(false)}
            disabled={deleting}
          >
            Keep alert
          </SecondaryDialogButton>
          <PrimaryDialogButton
            onClick={handleDelete}
            busy={deleting}
            disabled={!typedMatches || deleting}
            Icon={Trash2}
            variant="pearl"
          >
            Delete forever
          </PrimaryDialogButton>
        </>
      }
    >
      <div
        className={`${RADIUS.chip} border ${DESTRUCTIVE.ring} bg-slate-950/60 px-3 py-3 flex flex-col gap-2`}
      >
        <div className="flex items-start gap-2">
          <TriangleAlert className="w-4 h-4 text-slate-100 shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-sm font-semibold text-slate-50 leading-tight">
              This cannot be undone.
            </span>
            <span className="text-[12px] text-slate-200/85 leading-relaxed">
              We will remove the alert&apos;s scheduled job, delete the forked
              notebook, and drop its linked session. Previous email deliveries
              sent to your inbox are not affected.
            </span>
          </div>
        </div>
      </div>

      <div>
        <label className={DIALOG_LABEL_CLASS} htmlFor="delete-confirm">
          Type <span className="text-sky-100 font-semibold">{alert.name}</span> to confirm
        </label>
        <input
          id="delete-confirm"
          className={getDialogInputClass(viewMode)}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={alert.name}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          onKeyDown={(e) => {
            if (e.key === "Enter" && typedMatches) {
              e.preventDefault();
              handleDelete();
            }
          }}
        />
      </div>

      {error && (
        <div className={DIALOG_ERROR_CLASS}>
          <TriangleAlert className="w-3.5 h-3.5" />
          <span>{error}</span>
        </div>
      )}
    </IcyDialogShell>
  );
}
